/**
 * Embedded OHIF viewer used by the PMS Charting page (Phase 7).
 *
 * Behavior states (matching wireframe Row 6, screenshots 2–7):
 *   1. Empty   — drop zone with "local storage" / "cloud storage" choice.
 *   2. Loading — dimmed overlay with global enamel-tooth loader.
 *   3. Loaded  — PMS-side context strip + OHIF iframe in embedded mode
 *                (no OHIF header, no left/right panels — `?embedded=1`).
 *
 * Owns the iframe lifecycle and bridges PMS → OHIF commands (e.g. tooth click
 * → `navigate-to-study`) and OHIF → PMS events through `useEmbeddedBridge`.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchPatientFolder } from '@store/actions/folder.action'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { getEnv } from '@utils/env'
import { uploadFile } from '@services/api/folder.api'
import {
  IMAGE_VIEWER_MESSAGE_SOURCE,
  isImageViewerMessageEnvelope,
  type OhifToPmsMessage,
  type OpenNotesForImageEvent,
  type PmsToOhifMessage,
} from '@utils/imageViewerMessages'
import { NoteContextType } from '@dataTypes/note.type'
import './EmbeddedXrayViewer.css'

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024
const ACCEPTED_EXTENSIONS = ['.dcm', '.dicom', '.png', '.jpg', '.jpeg']
const LOADING_CONTAINER_ID = 'embedded-xray-viewer-loading'

export interface EmbeddedXraySelectedFile {
  studyInstanceUID?: string
  seriesInstanceUID?: string
  filename: string
}

export interface EmbeddedXrayViewerProps {
  /** Selected file(s) from /files cloud-storage path or local upload result. */
  initialFile?: EmbeddedXraySelectedFile | null
  initialFiles?: EmbeddedXraySelectedFile[] | null
  /** Called whenever the embedded viewer transitions to the loaded state. */
  onFileLoaded?: (file: EmbeddedXraySelectedFile) => void
  /**
   * False while the host charting tab is mounted but not shown. Prevents OHIF
   * from enabling Cornerstone in a hidden pane (black DICOM canvas).
   */
  isHostVisible?: boolean
}

export interface EmbeddedXrayViewerHandle {
  /** Programmatically swap the viewport to a different study/series. */
  navigateToStudy: (
    studyInstanceUID: string,
    seriesInstanceUID?: string,
  ) => void
  /** Currently-loaded file metadata, if any. */
  getSelectedFile: () => EmbeddedXraySelectedFile | null
}

/** Cornerstone cannot recover from enableElement() at 0×0 (black canvas). */
const hasRenderableBox = (el: HTMLElement | null): boolean =>
  !!el && el.clientWidth > 1 && el.clientHeight > 1

const pickLayoutHint = (count: number): string | null => {
  if (count >= 4) return '2x2'
  if (count === 3) return '1x3'
  if (count === 2) return '1x2'
  return null
}

const buildIframeSrc = (
  ohifUrl: string,
  files: EmbeddedXraySelectedFile[],
  token: string,
  patientId: string,
): string => {
  // Mirrors the multi-viewport URL shape used by `ImageXrayViewer.page.tsx`
  // (one `StudyInstanceUIDs=` per selected file + optional layout hint) so
  // the dental hanging protocol opens one viewport per file. JPG/PNG files
  // are first converted to DICOM at upload time, so they carry the same
  // study/series UIDs as native DCM files.
  const queryParts: string[] = []
  const uniqueStudyUids: string[] = []
  const seen = new Set<string>()
  files.forEach((f) => {
    if (f.studyInstanceUID && !seen.has(f.studyInstanceUID)) {
      seen.add(f.studyInstanceUID)
      uniqueStudyUids.push(f.studyInstanceUID)
    }
  })
  uniqueStudyUids.forEach((sid) => {
    queryParts.push(`StudyInstanceUIDs=${sid}`)
  })
  // When every file belongs to the same study but different series, expose
  // each series so the hanging protocol can place them in distinct
  // viewports. Skip when we're already loading multiple studies.
  if (uniqueStudyUids.length <= 1) {
    files.forEach((f) => {
      if (f.seriesInstanceUID) {
        queryParts.push(`SeriesInstanceUIDs=${f.seriesInstanceUID}`)
      }
    })
  }
  queryParts.push(`token=${encodeURIComponent(token || '')}`)
  queryParts.push(`patient_id=${encodeURIComponent(patientId || '')}`)
  // `embedded=1` is the Phase 7 chromeless variant — see OHIF
  // ViewerLayout/index.tsx (also aliased to chromeless behavior).
  queryParts.push('embedded=1')
  queryParts.push('chromeless=1')
  const layoutHint = pickLayoutHint(files.length)
  if (layoutHint) queryParts.push(`layout=${layoutHint}`)
  return `${ohifUrl}/dental?${queryParts.join('&')}`
}

const EmbeddedXrayViewer = forwardRef<
  EmbeddedXrayViewerHandle,
  EmbeddedXrayViewerProps
>(function EmbeddedXrayViewer(
  { initialFile, initialFiles, onFileLoaded, isHostVisible = true },
  ref,
) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const viewportWrapRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [viewportHasSize, setViewportHasSize] = useState(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const token = useAppSelector((state) => state.auth.accessToken)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const xrayFolderId = useAppSelector((state) => {
    const childFolders = state.folder.childFolders || []
    const xray = childFolders.find(
      (f) => f.folder_name?.toLowerCase() === 'x-ray files',
    )
    return xray?.folder_id ?? state.folder.selectedFolder?.folder_id ?? ''
  })

  const OHIF_URL = getEnv('REACT_APP_OHIF_URL', 'http://localhost:3000')

  // Populate the shared folder slice for the active patient so `xrayFolderId`
  // can resolve to the "X-ray Files" folder. Unlike the standalone X-ray
  // viewer page, the Charting page never fetches the patient's folders, which
  // left `childFolders` empty and caused uploads to omit `folder_id` (backend
  // rejects with "folder_id is required").
  useEffect(() => {
    if (selectedPatient?.patient_id) {
      dispatch(fetchPatientFolder(selectedPatient.patient_id))
    }
  }, [dispatch, selectedPatient?.patient_id])

  const normalizedInitial = useMemo<EmbeddedXraySelectedFile[]>(() => {
    if (initialFiles && initialFiles.length > 0) return initialFiles
    if (initialFile) return [initialFile]
    return []
  }, [initialFile, initialFiles])

  const [selectedFiles, setSelectedFiles] =
    useState<EmbeddedXraySelectedFile[]>(normalizedInitial)
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(
    normalizedInitial.length > 0,
  )
  const [isDragOver, setIsDragOver] = useState(false)

  useEffect(() => {
    if (normalizedInitial.length > 0) {
      setSelectedFiles(normalizedInitial)
      setIsLoadingImage(true)
    }
  }, [normalizedInitial])

  // When the active patient is removed/cleared, reset to the empty state so
  // the viewer shows the local/cloud upload chooser instead of holding the
  // previous patient's loaded DICOM (the parent clears `initialFiles`, but
  // this component owns `selectedFiles` and doesn't unmount while the panel
  // stays open).
  useEffect(() => {
    if (!selectedPatient?.patient_id) {
      setSelectedFiles([])
      setIsLoadingImage(false)
    }
  }, [selectedPatient?.patient_id])

  const primaryFile: EmbeddedXraySelectedFile | null = selectedFiles[0] ?? null

  // Keep the latest selection available to the (stable) postMessage callbacks
  // without re-creating them whenever the selection changes.
  const selectedFilesRef = useRef<EmbeddedXraySelectedFile[]>(selectedFiles)
  useEffect(() => {
    selectedFilesRef.current = selectedFiles
  }, [selectedFiles])

  // While the iframe parses the study we intentionally do NOT show the PMS
  // global enamel-tooth loader: the embedded OHIF viewer renders its own
  // tooth loader, and stacking both produced two loading screens. We still
  // clear `isLoadingImage` on a fallback timeout in case OHIF never emits
  // `viewer-ready`.
  useEffect(() => {
    if (isLoadingImage) {
      const timeout = window.setTimeout(() => {
        setIsLoadingImage(false)
      }, 30000)
      return () => {
        window.clearTimeout(timeout)
      }
    }
    return undefined
  }, [isLoadingImage])

  const sendCommand = useCallback(
    (message: PmsToOhifMessage): void => {
      const target = iframeRef.current?.contentWindow
      if (!target) return
      try {
        const origin = new URL(OHIF_URL).origin
        target.postMessage(
          { source: IMAGE_VIEWER_MESSAGE_SOURCE, message },
          origin,
        )
      } catch {
        // ignore origin parse failures
      }
    },
    [OHIF_URL],
  )

  // Push the PMS-known filename(s) into the OHIF iframe so it can label the
  // viewer (and the "Add note" suggested title) with the real filename instead
  // of falling back to `series_<seriesInstanceUID>`. Mirrors `sendFileMetadata`
  // in `ImageXrayViewer.page.tsx`; the embedded viewer previously never sent
  // this, which is why the note title showed the raw series UID.
  const sendFileMetadata = useCallback((): void => {
    const files = selectedFilesRef.current || []
    const filesMeta = files
      .filter((f) => f.seriesInstanceUID)
      .map((f) => ({
        seriesUID: f.seriesInstanceUID as string,
        fileName: f.filename || '',
        originalName: f.filename || '',
        studyUID: f.studyInstanceUID || undefined,
      }))
    if (filesMeta.length === 0) return
    sendCommand({ type: 'set-file-metadata', payload: { files: filesMeta } })
  }, [sendCommand])

  // Ref-bag of latest event-handler dependencies. Keeps the message
  // listener stable while always seeing fresh patient / folder context.
  const messageHandlerCtxRef = useRef({
    navigate,
    selectedPatientId: selectedPatient?.patient_id,
    xrayFolderId,
    returnTo: location.pathname,
    selectedFiles,
  })
  useEffect(() => {
    messageHandlerCtxRef.current = {
      navigate,
      selectedPatientId: selectedPatient?.patient_id,
      xrayFolderId,
      returnTo: location.pathname,
      selectedFiles,
    }
  }, [
    navigate,
    selectedPatient?.patient_id,
    xrayFolderId,
    location.pathname,
    selectedFiles,
  ])

  // Listen for OHIF → PMS events. Mirrors the handlers in
  // `ImageXrayViewer.page.tsx` so behaviors like "Add note for this image"
  // and "Check Previous X-Rays" work identically when the viewer is
  // embedded in the Charting page.
  useEffect(() => {
    let ohifOrigin: string
    try {
      ohifOrigin = new URL(OHIF_URL).origin
    } catch {
      return undefined
    }

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== ohifOrigin) return
      if (!isImageViewerMessageEnvelope(event.data)) return
      const ctx = messageHandlerCtxRef.current
      const msg = event.data.message as OhifToPmsMessage
      switch (msg.type) {
        case 'viewer-ready':
          // OHIF emits this once the first image has actually rendered, so we
          // dismiss the loading overlay immediately instead of waiting on the
          // fallback timeout.
          setIsLoadingImage(false)
          // Re-send metadata now that the viewer is up — the iframe clears its
          // seriesUID→filename map on (re)load.
          sendFileMetadata()
          break
        case 'request-file-metadata':
          sendFileMetadata()
          break
        case 'annotation-saved':
        case 'report-submitted':
        case 'viewer-closed':
          setIsLoadingImage(false)
          break
        case 'check-previous-xrays':
        case 'open-cloud-storage':
          ctx.navigate('/files', {
            state: {
              from: 'charting-xray-panel',
              patientId: ctx.selectedPatientId,
              defaultFolderId: ctx.xrayFolderId || 'default-0',
              mode: 'select-xray',
              accept: ACCEPTED_EXTENSIONS,
              returnTo: ctx.returnTo,
            },
          })
          break
        case 'open-notes-for-image': {
          const payload = (msg as OpenNotesForImageEvent).payload
          // Carry the currently-loaded X-ray file(s) and the panel-open flag
          // inside `previousPage`. When the user taps "Back to charting" the
          // notes editor navigates to `previousPage.path` spreading the whole
          // object into route state, so Charting's route-state effect can
          // re-open the panel and reload the same DICOM instead of returning
          // to the empty drop-zone.
          ctx.navigate('/notes', {
            state: {
              patientId: payload.patientId || undefined,
              contextType: NoteContextType.FILES,
              contextName: payload.imageFilename || 'X-ray Image',
              initialTitle: payload.suggestedTitle || undefined,
              openFirstNote: true,
              previousPage: {
                path: ctx.returnTo || '/charting',
                xrayPanelOpen: true,
                selectedFiles: ctx.selectedFiles,
              },
            },
          })
          break
        }
        default:
          break
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [OHIF_URL, sendFileMetadata])

  // Proactively push metadata for the first few seconds after a new selection
  // loads. Covers the race where the iframe's message listener isn't yet
  // registered when the first broadcast is posted (OHIF also pulls via
  // `request-file-metadata`, but this guards against a missed request).
  useEffect(() => {
    if (selectedFiles.length === 0) return undefined
    sendFileMetadata()
    const interval = window.setInterval(sendFileMetadata, 500)
    const stop = window.setTimeout(() => window.clearInterval(interval), 5000)
    return () => {
      window.clearInterval(interval)
      window.clearTimeout(stop)
    }
  }, [selectedFiles, sendFileMetadata])

  useImperativeHandle(
    ref,
    (): EmbeddedXrayViewerHandle => ({
      navigateToStudy: (studyInstanceUID, seriesInstanceUID) => {
        if (!studyInstanceUID) return
        sendCommand({
          type: 'navigate-to-study',
          payload: { studyInstanceUID, seriesInstanceUID },
        })
        setIsLoadingImage(true)
      },
      getSelectedFile: () => primaryFile,
    }),
    [sendCommand, primaryFile],
  )

  const handleLocalStorageClick = useCallback((): void => {
    fileInputRef.current?.click()
  }, [])

  const handleCloudStorageClick = useCallback((): void => {
    navigate('/files', {
      state: {
        from: 'charting-xray-panel',
        patientId: selectedPatient?.patient_id,
        defaultFolderId: xrayFolderId || 'default-0',
        mode: 'select-xray',
        accept: ACCEPTED_EXTENSIONS,
        returnTo: location.pathname,
      },
    })
  }, [navigate, location.pathname, selectedPatient?.patient_id, xrayFolderId])

  const validateAndUpload = useCallback(
    async (file: File): Promise<void> => {
      const name = file.name || ''
      const lower = name.toLowerCase()
      const hasAccepted = ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
      if (!hasAccepted) {
        showToast(
          createFailedToast(
            'Unsupported File',
            `Only ${ACCEPTED_EXTENSIONS.join(', ')} are supported.`,
          ),
        )
        return
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        showToast(
          createFailedToast('File Too Large', 'Maximum file size is 100 MB.'),
        )
        return
      }
      if (!selectedPatient?.patient_id) {
        showToast(
          createFailedToast(
            'No Patient Selected',
            'Select a patient before uploading X-ray files.',
          ),
        )
        return
      }

      showLoading({ container: LOADING_CONTAINER_ID })
      try {
        const response = await uploadFile(
          file,
          selectedPatient.patient_id,
          xrayFolderId || undefined,
        )
        if (response.statusCode >= 200 && response.statusCode < 300) {
          const uploaded = response.data
          const next: EmbeddedXraySelectedFile = {
            filename: uploaded?.file_name || name,
            studyInstanceUID: uploaded?.study_uid || undefined,
            seriesInstanceUID: uploaded?.series_uid || undefined,
          }
          setSelectedFiles([next])
          setIsLoadingImage(true)
          onFileLoaded?.(next)
        } else {
          showToast(
            createFailedToast(
              'Upload Failed',
              response.message || 'Failed to upload X-ray file.',
            ),
          )
        }
      } catch (err: any) {
        showToast(
          createFailedToast(
            'Upload Failed',
            err?.message || 'Failed to upload X-ray file.',
          ),
        )
      } finally {
        hideLoading(LOADING_CONTAINER_ID)
      }
    },
    [
      hideLoading,
      onFileLoaded,
      selectedPatient?.patient_id,
      showLoading,
      showToast,
      xrayFolderId,
    ],
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (file) {
        void validateAndUpload(file)
      }
    },
    [validateAndUpload],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault()
      setIsDragOver(false)
      const file = event.dataTransfer.files?.[0]
      if (file) {
        void validateAndUpload(file)
      }
    },
    [validateAndUpload],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault()
      setIsDragOver(true)
    },
    [],
  )

  const handleDragLeave = useCallback((): void => {
    setIsDragOver(false)
  }, [])

  const iframeSrc = useMemo(() => {
    if (selectedFiles.length === 0 || !selectedPatient?.patient_id) return null
    return buildIframeSrc(
      OHIF_URL,
      selectedFiles,
      token || '',
      selectedPatient.patient_id,
    )
  }, [OHIF_URL, selectedFiles, selectedPatient?.patient_id, token])

  // Delay attaching the OHIF iframe until this pane has a real layout box.
  // Hidden charting tabs used to stay mounted with `display: none`, so
  // Cornerstone enabled the viewport at 0×0 and the DICOM stayed black after
  // switching Dental → Plan (`Viewport is too small 0 0`).
  useLayoutEffect(() => {
    const el = viewportWrapRef.current
    if (!el) {
      setViewportHasSize(false)
      return undefined
    }

    const measure = (): void => {
      const next = hasRenderableBox(el)
      setViewportHasSize((prev) => (prev === next ? prev : next))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [iframeSrc, isHostVisible])

  const showOhifIframe = Boolean(iframeSrc) && viewportHasSize && isHostVisible

  const handleCheckPreviousXrays = useCallback((): void => {
    // Defer to the existing PMS X-ray browser flow by navigating the user
    // to the Files page in select-xray mode. Phase 7.2.8 will swap this
    // for an inline XrayStudyBrowser modal once the modal accepts a
    // single-select callback that posts `navigate-to-study`.
    handleCloudStorageClick()
  }, [handleCloudStorageClick])

  const showEmptyState = selectedFiles.length === 0
  const filenameLabel =
    selectedFiles.length > 1
      ? `${primaryFile?.filename ?? ''} (+${selectedFiles.length - 1} more)`
      : (primaryFile?.filename ?? '')

  return (
    <div className="embedded-xray-viewer d-flex flex-column h-100 w-100">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {!showEmptyState && (
        <div className="embedded-xray-context-strip d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="embedded-xray-link-button"
            onClick={handleCheckPreviousXrays}
          >
            Check Previous X-Rays
          </button>
          <span className="embedded-xray-filename" title={filenameLabel}>
            {filenameLabel}
          </span>
        </div>
      )}

      <div
        ref={viewportWrapRef}
        className={`embedded-xray-viewport-wrap position-relative flex-grow-1 ${
          showEmptyState ? 'embedded-xray-viewport-wrap--empty' : ''
        }`}
        id={LOADING_CONTAINER_ID}
        onDrop={showEmptyState ? handleDrop : undefined}
        onDragOver={showEmptyState ? handleDragOver : undefined}
        onDragLeave={showEmptyState ? handleDragLeave : undefined}
      >
        {showEmptyState ? (
          <div
            className={`embedded-xray-dropzone d-flex flex-column align-items-center justify-content-center h-100 w-100 ${
              isDragOver ? 'embedded-xray-dropzone--active' : ''
            }`}
          >
            <i
              className="ti ti-cloud-upload embedded-xray-dropzone-icon"
              aria-hidden
            />
            <p className="embedded-xray-dropzone-text mb-0">
              <em>Drag and drop file here or choose file from </em>
              <button
                type="button"
                className="embedded-xray-link-inline"
                onClick={handleLocalStorageClick}
              >
                local storage
              </button>
              <em> or </em>
              <button
                type="button"
                className="embedded-xray-link-inline"
                onClick={handleCloudStorageClick}
              >
                cloud storage
              </button>
            </p>
          </div>
        ) : showOhifIframe && iframeSrc ? (
          <iframe
            key={iframeSrc}
            ref={iframeRef}
            src={iframeSrc}
            className="embedded-xray-iframe"
            title="Embedded X-ray Viewer"
            allow="cross-origin-isolated"
            sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
          />
        ) : null}
      </div>
    </div>
  )
})

export default EmbeddedXrayViewer
